import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminModal, BTN_BLACK, BTN_WHITE, INPUT, errText } from "@/components/pageSettings/AdminModal";

const KINDS = { meta: "Meta pixel", google_ads: "Google Ads", ga4: "Google Analytics 4", tiktok: "TikTok pixel", custom: "Other (paste code)" };
const WHERE = { all: "Every public page", landers: "All landing pages", thank_you: "Thank-you pages only", pages: "Only these pages" };
const EMPTY = { name: "", kind: "meta", pixel_id: "", conversion_label: "", code: "", where: "all", page_slugs: [], enabled: true };

function PixelForm({ row, onClose, onSaved }) {
  const [f, setF] = useState(row ? { ...EMPTY, ...row } : EMPTY);
  const [slugs, setSlugs] = useState((row?.page_slugs || []).join(", "));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    const body = { ...f, page_slugs: slugs.split(/[\s,]+/).filter(Boolean) };
    ;["id", "created_at", "updated_at", "created_by", "scan_warnings"].forEach((k) => delete body[k]);
    try {
      const { data } = row ? await api.put(`/admin/site-pixels/${row.id}`, body) : await api.post("/admin/site-pixels", body);
      if (data.scan_warnings?.length) toast.warning(`Saved, but check this code: ${data.scan_warnings.join(", ")}`);
      onSaved();
    } catch (e) {
      toast.error(errText(e, "Couldn't save the pixel"));
    } finally {
      setBusy(false);
    }
  };
  const footer = (
    <>
      <button type="button" className={BTN_WHITE} onClick={onClose} data-testid="site-pixel-cancel">Cancel</button>
      <button type="button" className={BTN_BLACK} onClick={save} disabled={busy || !f.name.trim()} data-testid="site-pixel-save">
        {busy ? "Saving…" : "Save pixel"}
      </button>
    </>
  );
  return (
    <AdminModal eyebrow="Company pixel" title={row ? row.name : "Add a company pixel"} onClose={onClose} footer={footer} testId="site-pixel-dialog">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs">Name
          <input className={INPUT} value={f.name} maxLength={80} onChange={(e) => set("name", e.target.value)} data-testid="site-pixel-name" />
        </label>
        <label className="text-xs">Type
          <select className={INPUT} value={f.kind} onChange={(e) => set("kind", e.target.value)} data-testid="site-pixel-kind">
            {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        {f.kind === "custom" ? (
          <label className="text-xs sm:col-span-2">Pixel code
            <textarea className={`${INPUT} h-32 font-mono text-[11px]`} value={f.code || ""} onChange={(e) => set("code", e.target.value)} data-testid="site-pixel-code" />
          </label>
        ) : (
          <label className="text-xs">ID
            <input className={`${INPUT} font-mono`} value={f.pixel_id || ""} onChange={(e) => set("pixel_id", e.target.value)} data-testid="site-pixel-id" />
          </label>
        )}
        {f.kind === "google_ads" && (
          <label className="text-xs">Conversion label (for leads)
            <input className={`${INPUT} font-mono`} value={f.conversion_label || ""} onChange={(e) => set("conversion_label", e.target.value)} data-testid="site-pixel-label" />
          </label>
        )}
        <label className="text-xs">Where it loads
          <select className={INPUT} value={f.where} onChange={(e) => set("where", e.target.value)} data-testid="site-pixel-where">
            {Object.entries(WHERE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        {f.where === "pages" && (
          <label className="text-xs sm:col-span-2">Page addresses (the part after /lp/, comma separated)
            <input className={`${INPUT} font-mono`} value={slugs} onChange={(e) => setSlugs(e.target.value)} placeholder="standard, auto, pinnacle-auto" data-testid="site-pixel-pages" />
          </label>
        )}
      </div>
      <p className="mt-3 text-[11px] text-zinc-600">
        Loads after the page has finished loading, so it never slows the first view. On thank-you pages it also records the lead.
      </p>
    </AdminModal>
  );
}

function PixelRow({ p, canEdit, onEdit, onChanged }) {
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errText(e, "That didn't work")); }
  };
  const t = `site-pixel-${p.id}`;
  return (
    <tr className="border-b border-zinc-200" data-testid={`${t}-row`}>
      <td className="px-3 py-2 font-semibold">{p.name}{!p.enabled && <span className="ml-1 text-[10px] text-zinc-500">(paused)</span>}{p.scan_warnings?.length > 0 && <span className="ml-1 text-[10px] text-amber-700" title={p.scan_warnings.join(", ")} data-testid={`${t}-warn`}>(check code)</span>}</td>
      <td className="px-3 py-2">{KINDS[p.kind]}</td>
      <td className="px-3 py-2 font-mono text-[11px]">{p.kind === "custom" ? "custom code" : p.pixel_id}</td>
      <td className="px-3 py-2">{WHERE[p.where]}{p.where === "pages" && `: ${p.page_slugs.join(", ")}`}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {canEdit && (
          <span className="inline-flex gap-1">
            <button type="button" className={BTN_WHITE} onClick={() => onEdit(p)} data-testid={`${t}-edit`}>Edit</button>
            <button type="button" className={BTN_WHITE} data-testid={`${t}-toggle`}
              onClick={() => act(() => api.put(`/admin/site-pixels/${p.id}`, { ...p, enabled: !p.enabled }), p.enabled ? "Paused" : "Turned on")}>
              {p.enabled ? "Pause" : "Resume"}
            </button>
            <button type="button" className={`${BTN_WHITE} text-red-700`} data-testid={`${t}-delete`}
              onClick={() => window.confirm(`Remove "${p.name}"?`) && act(() => api.delete(`/admin/site-pixels/${p.id}`), "Removed")}>
              Delete
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}

/** As many extra company pixels as needed, each placed where it should load. */
export function SitePixelsAdmin() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const load = useCallback(() => {
    api.get("/admin/site-pixels").then((r) => setRows(r.data.pixels)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const clean = (p) => { const { created_at, updated_at, created_by, scan_warnings, id, ...rest } = p; return { ...rest, id }; };
  return (
    <section className="mt-8 border-2 border-black bg-white p-4" data-testid="site-pixels-admin">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="label-mono">More company pixels</div>
          <p className="mt-1 max-w-3xl text-xs text-zinc-700">
            Add as many of your own pixels as you need and choose where each one loads. Affiliate, vendor and media
            buyer tracking never loads here: it runs from our server through their Postbacks page.
          </p>
        </div>
        {canEdit && (
          <button type="button" className={BTN_BLACK} onClick={() => setEdit(null)} data-testid="site-pixel-add">
            <Plus size={11} weight="bold" /> Add pixel
          </button>
        )}
      </div>
      {rows === null ? <div className="label-mono mt-3">Loading…</div> : rows.length === 0 ? (
        <div className="label-mono mt-3 py-4 text-center" data-testid="site-pixels-empty">No extra pixels yet</div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs" data-testid="site-pixels-table">
            <tbody>{rows.map((p) => <PixelRow key={p.id} p={clean(p)} canEdit={canEdit} onEdit={setEdit} onChanged={load} />)}</tbody>
          </table>
        </div>
      )}
      {edit !== undefined && <PixelForm row={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); toast.success("Pixel saved"); load(); }} />}
    </section>
  );
}
